import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon'
import Text from '@embracesbs/component-text'
import TextField from '@embracesbs/component-textfield'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper/TextFieldWrapper'
import Autocomplete from '@embracesbs/component-autocomplete'
import Accordion from '@embracesbs/component-accordion/Accordion'
import Divider from '@embracesbs/component-divider'
import EmptyState from '@embracesbs/component-emptystate'
import { customToast } from '@embracesbs/component-toast'
import Toast from '@embracesbs/component-toast'
import { useApi } from '../Hooks'
import LoadingState from '../Components/Common/LoadingState'
import BandBackButton from '../Components/Common/BandBackButton'

// Mirrors tenants.shared RolloutStatus / RolloutItemStatus.
const RolloutStatus = { Scheduled: 0, Running: 1, Paused: 2, Stopping: 3, Stopped: 4, Fulfilled: 5 } as const
const ItemStatus = { Pending: 0, Running: 1, Succeeded: 2, Failed: 3, Skipped: 4 } as const

const STATUS_LABEL: Record<number, string> = {
  0: 'Scheduled',
  1: 'Running',
  2: 'Paused',
  3: 'Stopping',
  4: 'Stopped',
  5: 'Fulfilled'
}
// Status tokens so the accessibility themes can substitute hues (index.css).
// These are var() strings — derive tints with color-mix, never hex suffixes.
const STATUS_COLOR: Record<number, string> = {
  0: 'var(--tma-warning-text)',
  1: 'var(--tma-accent)',
  2: 'var(--tma-warning-text)',
  3: 'var(--tma-warning-text)',
  4: '#6b7280',
  5: 'var(--tma-status-ok-strong)'
}

// Small tinted pill for status labels and count chips (inherits the app font).
const pill = (color: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  color,
  background: `color-mix(in srgb, ${color} 12%, transparent)`,
  borderRadius: '999px',
  padding: '2px 8px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap'
})

// The design-system Accordion defaults to ~24px padding on its header and
// content; tighten it (values are px). The content's 6px top padding gives the
// same gap above the first tenant row as the 6px between rows (their
// marginBottom); bottom stays 0 since the last row's marginBottom covers it.
const ACCORDION_SPACING = { top: 8, right: 12, bottom: 8, left: 12 }
const ACCORDION_CONTENT_SPACING = { top: 6, right: 12, bottom: 0, left: 12 }

// Spell out each non-excluded state that has tenants, in the completion bar's
// left-to-right segment order (e.g. "8 completed · 1 failed"). Shared by the bar
// and the history cards so they read the same. Excluded/zero states are dropped.
const stateSummary = (counts: { completed: number; failed: number; running: number; queued: number }): string =>
  [
    { n: counts.completed, label: 'completed' },
    { n: counts.failed, label: 'failed' },
    { n: counts.running, label: 'in progress' },
    { n: counts.queued, label: 'in queue' }
  ]
    .filter((p) => p.n > 0)
    .map((p) => `${p.n} ${p.label}`)
    .join(' · ')

interface RolloutItem {
  tenantId: string
  tenantName: string
  status: number
  startedAtUtc?: string | null
  finishedAtUtc?: string | null
  error?: string | null
  subscriptionId?: string | null
  buildDefinitionId?: number | null
}
// A lifecycle group; drives both the summary pill and the accordion so they
// stay in sync (one entry per RolloutItemStatus we surface).
interface SectionDef {
  key: string
  label: string
  status: number
  items: RolloutItem[]
  empty: string
}
interface RolloutSummary {
  id: string
  environment: string
  status: number
  maxConcurrency: number
  updateMode: string
  scheduledStartUtc?: string | null
  createdBy?: string | null
  createdAtUtc: string
  startedAtUtc?: string | null
  endedAtUtc?: string | null
  total: number
  pending: number
  running: number
  succeeded: number
  failed: number
  skipped: number
}
interface RolloutDetail extends Omit<RolloutSummary, 'total' | 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped'> {
  items: RolloutItem[]
}

// The app font. Raw spans on this page would otherwise fall back to the
// browser serif (index.css sets no body font); the design-system Text applies
// this itself, so setting it on the page root keeps everything consistent.
const APP_FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

// Rollout timestamps are UTC but serialised without a trailing 'Z' (EF reads
// datetime2 back as Kind=Unspecified), so a bare string would be parsed as
// local time. Normalise to UTC before parsing, then show the admin's local time.
const parseUtc = (iso: string): Date => {
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso)
  return new Date(hasZone ? iso : `${iso}Z`)
}
const formatDateTime = (iso?: string | null): string => {
  if (!iso) return ''
  const d = parseUtc(iso)
  return isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

const isActive = (status: number) =>
  status === RolloutStatus.Scheduled ||
  status === RolloutStatus.Running ||
  status === RolloutStatus.Paused ||
  status === RolloutStatus.Stopping

const itemIcon = (status: number): { name: string; color: string; spin?: boolean } => {
  switch (status) {
    case ItemStatus.Succeeded:
      return { name: 'CheckSquare', color: 'var(--tma-status-ok-strong)' }
    case ItemStatus.Failed:
      return { name: 'AlertTriangle', color: 'var(--tma-status-failed-strong)' }
    case ItemStatus.Running:
      return { name: 'EmailActionSync1', color: 'var(--tma-accent)', spin: true }
    case ItemStatus.Skipped:
      return { name: 'SubtractCircle', color: '#9a9ba0' }
    default:
      return { name: 'TimeClockCircle', color: '#9a9ba0' }
  }
}

const StageRollout: React.FC = () => {
  const { fetchApi } = useApi()
  const navigate = useNavigate()

  // Open a tenant's detail page from a rollout row (deep link needs the
  // subscription id, resolved by the API); no-op if the tenant is gone.
  const openTenant = (item: RolloutItem) => {
    if (item.subscriptionId) navigate(`/subscriptions/${item.subscriptionId}/tenant/${item.tenantId}`)
  }

  // Open the tenant's Azure DevOps build pipeline (the failed run is the latest),
  // matching the "Tenant Pipeline" link in the tenant details.
  const openPipeline = (item: RolloutItem) => {
    if (item.buildDefinitionId) {
      window.open(
        `https://dev.azure.com/embrace/Embrace%20Suite/_build?definitionId=${item.buildDefinitionId}`,
        '_blank',
        'noopener,noreferrer'
      )
    }
  }

  const [segments, setSegments] = useState<string[]>([])
  const [rollouts, setRollouts] = useState<RolloutSummary[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<RolloutDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // Single-open accordion: which lifecycle group is expanded. Clicking a pill or
  // an accordion header sets this; '' means all collapsed.
  const [openSection, setOpenSection] = useState<string>('failed')

  // New-rollout form.
  const [environment, setEnvironment] = useState('')
  const [maxConcurrency, setMaxConcurrency] = useState('3')
  const [scheduleAt, setScheduleAt] = useState('')
  const [isStarting, setIsStarting] = useState(false)
  // Tenants the rollout would cover, and the ones the admin excluded.
  const [eligibleTenants, setEligibleTenants] = useState<{ id: string; tenantName: string }[]>([])
  const [excludedIds, setExcludedIds] = useState<string[]>([])

  useEffect(() => {
    fetchApi({ endpoint: '/api/featureflags/segments', method: 'GET' })
      .then((response) => setSegments(response || []))
      .catch((error) => console.error(error))
  }, [fetchApi])

  // Load the eligible tenants whenever the chosen stage changes; include all by default.
  useEffect(() => {
    if (!environment) {
      setEligibleTenants([])
      setExcludedIds([])
      return
    }
    fetchApi({ endpoint: `/api/rollout/eligible?environment=${encodeURIComponent(environment)}`, method: 'GET' })
      .then((response) => setEligibleTenants(Array.isArray(response) ? response : []))
      .catch((error) => console.error(error))
    setExcludedIds([])
  }, [environment, fetchApi])

  const loadRollouts = useCallback(async (): Promise<RolloutSummary[]> => {
    try {
      const response = await fetchApi({ endpoint: '/api/rollout', method: 'GET' })
      const data: RolloutSummary[] = Array.isArray(response) ? response : []
      setRollouts(data)
      return data
    } catch (error) {
      console.error(error)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [fetchApi])

  const loadDetail = useCallback(
    async (id: string) => {
      try {
        const response = await fetchApi({ endpoint: `/api/rollout/${id}`, method: 'GET' })
        setDetail(response)
      } catch (error) {
        console.error(error)
      }
    },
    [fetchApi]
  )

  // Initial load + poll the list; auto-select the newest active rollout.
  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      const list = await loadRollouts()
      if (cancelled) return
      setSelectedId((current) => current ?? list.find((r) => isActive(r.status))?.id ?? list[0]?.id ?? null)
    }
    tick()
    const interval = setInterval(tick, 5000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [loadRollouts])

  // Poll the selected rollout's detail.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      return
    }
    loadDetail(selectedId)
    const interval = setInterval(() => loadDetail(selectedId), 5000)
    return () => clearInterval(interval)
  }, [selectedId, loadDetail])

  const handleStart = async () => {
    if (!environment) {
      customToast.error('Choose a stage first')
      return
    }
    setIsStarting(true)
    try {
      const body: Record<string, unknown> = {
        environment,
        maxConcurrency: Math.max(1, parseInt(maxConcurrency, 10) || 1)
      }
      if (scheduleAt) body.scheduledStartUtc = new Date(scheduleAt).toISOString()
      if (excludedIds.length > 0) body.excludeTenantIds = excludedIds

      const created = await fetchApi({ endpoint: '/api/rollout', method: 'POST', body })
      customToast.success(`Rollout started for ${environment}`)
      setEnvironment('')
      setScheduleAt('')
      setEligibleTenants([])
      setExcludedIds([])
      const list = await loadRollouts()
      setSelectedId(created?.id ?? list.find((r) => isActive(r.status))?.id ?? null)
    } catch (error: any) {
      const message = error?.response?.data ?? 'Failed to start rollout'
      customToast.error(typeof message === 'string' ? message : 'Failed to start rollout')
      console.error(error)
    } finally {
      setIsStarting(false)
    }
  }

  const addExcluded = (id: string) => {
    if (id && !excludedIds.includes(id)) setExcludedIds((prev) => [...prev, id])
  }
  const removeExcluded = (id: string) => setExcludedIds((prev) => prev.filter((x) => x !== id))
  const excludedSet = new Set(excludedIds)
  const includedCount = eligibleTenants.filter((t) => !excludedSet.has(t.id)).length
  const tenantName = (id: string) => eligibleTenants.find((t) => t.id === id)?.tenantName ?? id
  // A stage that already has a non-terminal rollout can't get another one.
  const stageBlocked = !!environment && rollouts.some((r) => r.environment === environment && isActive(r.status))

  const control = async (action: 'pause' | 'resume' | 'stop') => {
    if (!selectedId) return
    try {
      await fetchApi({ endpoint: `/api/rollout/${selectedId}/${action}`, method: 'PUT', body: {} })
      await Promise.all([loadRollouts(), loadDetail(selectedId)])
    } catch (error) {
      customToast.error(`Could not ${action} the rollout`)
      console.error(error)
    }
  }

  const handleDelete = async () => {
    if (!selectedId) return
    try {
      await fetchApi({ endpoint: `/api/rollout/${selectedId}`, method: 'DELETE' })
      setSelectedId(null)
      setDetail(null)
      await loadRollouts()
      customToast.success('Rollout deleted')
    } catch (error) {
      customToast.error('Could not delete the rollout')
      console.error(error)
    }
  }

  const selectedSummary = rollouts.find((r) => r.id === selectedId) || null
  const isTerminal = selectedSummary
    ? selectedSummary.status === RolloutStatus.Fulfilled || selectedSummary.status === RolloutStatus.Stopped
    : false
  // Excluded tenants are recorded as Skipped; they never run, so they are left
  // out of the progress bar (its segments) and reported separately.
  const included = detail?.items.filter((i) => i.status !== ItemStatus.Skipped) ?? []
  const total = included.length
  // Each state's share of the bar; the grey track shows the still-pending rest.
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0)
  // Per-tenant items grouped by lifecycle stage; each renders as a foldable
  // section so a long rollout stays scannable (failures up top, excluded hidden).
  const failedItems = detail?.items.filter((i) => i.status === ItemStatus.Failed) ?? []
  const runningItems = detail?.items.filter((i) => i.status === ItemStatus.Running) ?? []
  const queuedItems = detail?.items.filter((i) => i.status === ItemStatus.Pending) ?? []
  const completedItems = detail?.items.filter((i) => i.status === ItemStatus.Succeeded) ?? []
  const excludedItems = detail?.items.filter((i) => i.status === ItemStatus.Skipped) ?? []

  // The label inside the bar spells out each non-excluded state that has tenants.
  const barText = stateSummary({
    completed: completedItems.length,
    failed: failedItems.length,
    running: runningItems.length,
    queued: queuedItems.length
  })

  // One entry per lifecycle group, in display order — shared by the pills and
  // the accordions so they always agree.
  const sections: SectionDef[] = [
    { key: 'failed', label: 'Failed', status: ItemStatus.Failed, items: failedItems, empty: 'There are no failed tenants.' },
    { key: 'running', label: 'In progress', status: ItemStatus.Running, items: runningItems, empty: 'There are no tenants in progress.' },
    { key: 'queued', label: 'In queue', status: ItemStatus.Pending, items: queuedItems, empty: 'There are no tenants in queue.' },
    { key: 'completed', label: 'Completed', status: ItemStatus.Succeeded, items: completedItems, empty: 'There are no completed tenants.' },
    { key: 'excluded', label: 'Excluded', status: ItemStatus.Skipped, items: excludedItems, empty: 'There are no excluded tenants.' }
  ]

  const renderItem = (item: RolloutItem) => {
    const icon = itemIcon(item.status)
    const clickable = !!item.subscriptionId
    // The "Excluded from rollout" note is informational, not a failure, so it
    // stays grey; real errors are red.
    const isError = item.status === ItemStatus.Failed
    // Failed tenants get a shortcut to their pipeline so the run can be inspected.
    const showPipeline = isError && !!item.buildDefinitionId
    return (
      <div
        key={item.tenantId}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => openTenant(item) : undefined}
        onKeyDown={clickable ? (e) => e.key === 'Enter' && openTenant(item) : undefined}
        title={clickable ? `Open ${item.tenantName}` : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '9px 12px',
          background: 'var(--tma-panel-bg)',
          border: '1px solid var(--tma-card-border)',
          borderLeft: `3px solid ${icon.color}`,
          borderRadius: '6px',
          marginBottom: '6px',
          cursor: clickable ? 'pointer' : 'default'
        }}
      >
        <Icon iconName={icon.name} size={16} color={icon.color} style={icon.spin ? { animation: 'spin 1s linear infinite' } : undefined} />
        <Text textStyle='body' content={item.tenantName} singleLine style={{ flex: 1 }} />
        {item.error && (
          <Text
            textStyle='description'
            content={item.error}
            style={{ color: isError ? 'var(--tma-danger-text)' : 'var(--tma-text-faint)' }}
            singleLine
            hasTooltipEllipsis
          />
        )}
        {showPipeline && (
          // Wrapper stops the click bubbling to the row (which opens the tenant).
          <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0, display: 'inline-flex' }}>
            <Button2
              buttonSize='30px'
              buttonRadius='8px'
              variant='outlined'
              // Muted so the failure message stays the loudest thing on the row.
              style={{ borderColor: 'var(--tma-card-border)' }}
              leftElement={<Icon iconName='ConstructionPipe1' size={14} color='var(--tma-text-muted)' />}
              text=''
              tooltip='Open pipeline'
              aria-label={`Open pipeline for ${item.tenantName}`}
              type='button'
              onClick={() => openPipeline(item)}
            />
          </span>
        )}
        {clickable && <Icon iconName='ArrowRight1' size={14} color='var(--tma-accent)' style={{ flexShrink: 0 }} />}
      </div>
    )
  }

  // Clickable summary pill for a lifecycle group: its status icon + count, always
  // shown (even at 0). Clicking opens that group's accordion and closes the rest.
  const renderStatePill = (s: SectionDef) => {
    const ic = itemIcon(s.status)
    const active = openSection === s.key
    return (
      <span
        key={s.key}
        role='button'
        tabIndex={0}
        onClick={() => setOpenSection(s.key)}
        onKeyDown={(e) => e.key === 'Enter' && setOpenSection(s.key)}
        title={`Show ${s.label.toLowerCase()} tenants`}
        style={{
          ...pill(ic.color),
          cursor: 'pointer',
          background: active ? `color-mix(in srgb, ${ic.color} 19%, transparent)` : `color-mix(in srgb, ${ic.color} 8%, transparent)`,
          boxShadow: active ? `inset 0 0 0 1px ${ic.color}` : 'none'
        }}
      >
        <Icon iconName={ic.name} size={14} color={ic.color} />
        {s.label} {s.items.length}
      </span>
    )
  }

  // A foldable group of tenant rows, using the same Accordion component as the
  // runtime settings. Always rendered (even empty) and controlled by openSection
  // so a pill click or a header click opens one group and closes the others.
  // Zero-count groups are dimmed so the populated ones (usually Failed) lead.
  const renderSection = (s: SectionDef) => (
    <div key={s.key} style={{ opacity: s.items.length === 0 ? 0.55 : 1 }}>
      <Accordion
        id={s.key}
        headerTitle={`${s.label} (${s.items.length})`}
        isFullWidth
        isOpen={openSection === s.key}
        onArrowToggle={(isOpen) => setOpenSection(isOpen ? s.key : '')}
        spacing={ACCORDION_SPACING}
        contentSpacing={ACCORDION_CONTENT_SPACING}
      >
        {s.items.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>{s.items.map(renderItem)}</div>
        ) : (
          <div style={{ padding: '20px 12px', textAlign: 'center' }}>
            <Text textStyle='description' textColor='grey' content={s.empty} />
          </div>
        )}
      </Accordion>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: 'var(--app-height, 100vh)', overflow: 'hidden', fontFamily: APP_FONT }}>
      <Toast />
      {/* Left column: dark band + new-rollout form + rollout list. */}
      <div style={{ width: '30%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div
          style={{
            flexShrink: 0,
            height: '52px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '0 10px',
            backgroundColor: 'var(--tma-band-bg)'
          }}
        >
          <BandBackButton />
          <span
            style={{
              color: 'var(--tma-band-text)',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
            }}
          >
            Stage rollout
          </span>
        </div>

        <div
          style={{
            flexShrink: 0,
            background: 'var(--tma-panel-bg)',
            borderBottom: '1px solid var(--tma-list-card-border)',
            padding: '14px'
          }}
        >
          <TextFieldWrapper labelText='Stage'>
            <Autocomplete
              placeholder='Select a stage…'
              value={environment}
              onChange={(e: { value: string }) => setEnvironment(e.value)}
              options={segments.map((s) => ({ label: s, value: s }))}
            />
          </TextFieldWrapper>

          {/* Everything below is optional — sensible defaults (3 at a time, start
              now, all tenants) let you just pick a stage and start. */}
          <Accordion id='rollout-optional' headerTitle='Optional settings' isFullWidth={true}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <TextFieldWrapper labelText='Max concurrent updates'>
                <TextField type='number' value={maxConcurrency} onChange={(e) => setMaxConcurrency(e.target.value)} />
              </TextFieldWrapper>
              <TextFieldWrapper labelText='Schedule start'>
                <TextField type='datetime-local' value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
              </TextFieldWrapper>

              {environment && eligibleTenants.length > 0 && (
                <TextFieldWrapper labelText='Exclude tenants (optional)'>
                  {/* Search to exclude — only excluded tenants are listed, so this
                      scales to many tenants. key resets the picker after each pick. */}
                  <Autocomplete
                    key={excludedIds.length}
                    placeholder='Search a tenant name to exclude…'
                    onChange={(e: { value: string }) => addExcluded(e.value)}
                    options={eligibleTenants.filter((t) => !excludedSet.has(t.id)).map((t) => ({ label: t.tenantName, value: t.id }))}
                  />
                  <Text
                    textStyle='description'
                    textColor='grey'
                    content={
                      excludedIds.length === 0
                        ? `All ${eligibleTenants.length} tenants will be updated.`
                        : `${includedCount} of ${eligibleTenants.length} tenants will be updated · ${excludedIds.length} excluded`
                    }
                    style={{ display: 'block', marginTop: '6px' }}
                  />
                  {excludedIds.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                      {excludedIds.map((id) => (
                        <span
                          key={id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'var(--tma-danger-tint)',
                            color: 'var(--tma-danger-text)',
                            borderRadius: '999px',
                            padding: '4px 6px 4px 11px',
                            fontSize: '13px',
                            fontWeight: 600
                          }}
                        >
                          {tenantName(id)}
                          <button
                            type='button'
                            aria-label={`Include ${tenantName(id)} again`}
                            onClick={() => removeExcluded(id)}
                            style={{
                              border: 'none',
                              background: 'none',
                              color: 'inherit',
                              cursor: 'pointer',
                              fontSize: '15px',
                              lineHeight: 1,
                              padding: '0 3px'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </TextFieldWrapper>
              )}
            </div>
          </Accordion>

          {stageBlocked ? (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'flex-start',
                  background: 'var(--tma-warning-tint)',
                  border: '1px solid rgba(184,134,11,0.4)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  marginBottom: '12px'
                }}
              >
                <Icon iconName='AlertTriangle' size={16} color='var(--tma-warning-text)' style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>
                  <strong>{environment}</strong> already has an active rollout. Finish or stop it before starting a new one.
                </span>
              </div>
              <Button2
                text='Rollout already running'
                leftElement={<Icon iconName='Lock1' size={16} />}
                isDisabled
                isFullWidth
                variant='outlined'
                type='button'
                buttonRadius='8px'
              />
            </>
          ) : (
            <Button2
              text={isStarting ? 'Starting…' : scheduleAt ? 'Schedule rollout' : 'Start rollout'}
              leftElement={<Icon iconName='SpaceRocketFlying' size={16} color='#ffffff' />}
              onClick={handleStart}
              isDisabled={isStarting || !environment || (eligibleTenants.length > 0 && includedCount === 0)}
              isFullWidth
              variant='contained'
              type='button'
              buttonRadius='8px'
            />
          )}
        </div>

        <div
          style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: 'var(--tma-page-bg)', padding: '8px' }}
          className='custom-scrollbar'
        >
          {rollouts.map((r) => (
            <div
              key={r.id}
              role='button'
              tabIndex={0}
              onClick={() => setSelectedId(r.id)}
              onKeyDown={(e) => e.key === 'Enter' && setSelectedId(r.id)}
              style={{
                cursor: 'pointer',
                background: 'var(--tma-panel-bg)',
                borderStyle: 'solid',
                borderWidth: '1px',
                borderColor: r.id === selectedId ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
                borderRadius: '6px',
                padding: '10px 12px',
                marginBottom: '6px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text textStyle='body' content={r.environment} style={{ fontWeight: 600 }} />
                <span style={pill(STATUS_COLOR[r.status])}>{STATUS_LABEL[r.status]}</span>
              </div>
              <Text
                textStyle='description'
                textColor='grey'
                content={stateSummary({ completed: r.succeeded, failed: r.failed, running: r.running, queued: r.pending })}
              />
              <Text
                textStyle='description'
                textColor='grey'
                content={
                  r.startedAtUtc
                    ? `Started ${formatDateTime(r.startedAtUtc)}`
                    : r.scheduledStartUtc
                      ? `Scheduled for ${formatDateTime(r.scheduledStartUtc)}`
                      : `Created ${formatDateTime(r.createdAtUtc)}`
                }
                style={{ display: 'block', marginTop: '2px' }}
              />
            </div>
          ))}
          {!isLoading && rollouts.length === 0 && (
            <Text textStyle='description' textColor='grey' content='No rollouts yet.' style={{ display: 'block', padding: '8px' }} />
          )}
        </div>
      </div>

      {/* Right column: selected rollout detail. */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--tma-panel-bg)',
          padding: '0 20px',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ flexShrink: 0, height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <Text textStyle='h6' style={{ fontSize: '16px' }}>
              {selectedSummary ? `Rollout: ${selectedSummary.environment}` : 'Rollout'}
            </Text>
            {selectedSummary && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ ...pill(STATUS_COLOR[selectedSummary.status]), marginRight: '4px' }}>
                  {STATUS_LABEL[selectedSummary.status]}
                </span>
                {selectedSummary.status === RolloutStatus.Running && (
                  <Button2
                    text='Pause'
                    onClick={() => control('pause')}
                    variant='outlined'
                    type='button'
                    buttonSize='30px'
                    buttonRadius='8px'
                  />
                )}
                {selectedSummary.status === RolloutStatus.Paused && (
                  <Button2
                    text='Resume'
                    onClick={() => control('resume')}
                    variant='outlined'
                    type='button'
                    buttonSize='30px'
                    buttonRadius='8px'
                  />
                )}
                {isActive(selectedSummary.status) && (
                  <Button2
                    text='Stop'
                    onClick={() => control('stop')}
                    variant='outlined'
                    isDanger
                    type='button'
                    buttonSize='30px'
                    buttonRadius='8px'
                  />
                )}
                {isTerminal && (
                  <Button2
                    leftElement={<Icon iconName='Bin1' size={14} color='var(--tma-danger-text)' />}
                    text='Delete'
                    onClick={handleDelete}
                    variant='outlined'
                    isDanger
                    type='button'
                    buttonSize='30px'
                    buttonRadius='8px'
                  />
                )}
              </div>
            )}
          </div>
          <div style={{ borderTop: '1px solid var(--tma-list-card-border)', margin: '0 -20px' }} />
        </div>

        {!selectedSummary ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState imageName='rocket' size='medium' title='No rollout selected' text='Start a rollout or pick one from the list.' />
          </div>
        ) : !detail ? (
          <LoadingState text='Loading rollout…' />
        ) : (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingTop: '14px' }}>
            {/* Progress summary */}
            <div style={{ flexShrink: 0, marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>{sections.map(renderStatePill)}</div>
                <Text textStyle='description' textColor='grey' content={`max ${detail.maxConcurrency} at a time`} />
              </div>
              {/* Stacked segments sized to each state's share, so one failure is a
                  thin red slice (not a red bar) and the make-up is visible while the
                  rollout runs. Excluded tenants aren't counted; the grey track is the
                  still-pending remainder. */}
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  height: '20px',
                  borderRadius: '999px',
                  background: 'var(--tma-active-bg)',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${pct(completedItems.length)}%`,
                    flexShrink: 0,
                    background: 'var(--tma-status-ok-strong)',
                    transition: 'width 0.3s ease'
                  }}
                />
                <div
                  style={{
                    width: `${pct(failedItems.length)}%`,
                    flexShrink: 0,
                    background: 'var(--tma-status-failed-strong)',
                    transition: 'width 0.3s ease'
                  }}
                />
                <div
                  style={{
                    width: `${pct(runningItems.length)}%`,
                    flexShrink: 0,
                    background: 'var(--tma-accent)',
                    transition: 'width 0.3s ease'
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#ffffff',
                    textShadow: '0 0 3px rgba(0,0,0,0.55)'
                  }}
                >
                  {barText}
                </span>
              </div>
              {/* createdAtUtc is when the rollout was set up (for a scheduled run,
                  when the schedule was placed) — label it "Scheduled by" in that
                  case; the actual start is startedAtUtc. */}
              {detail.createdBy && (
                <Text
                  textStyle='description'
                  textColor='grey'
                  content={`${detail.scheduledStartUtc ? 'Scheduled' : 'Started'} by ${detail.createdBy} · ${formatDateTime(detail.createdAtUtc)}`}
                  style={{ display: 'block', marginTop: '6px' }}
                />
              )}
              {detail.scheduledStartUtc && detail.startedAtUtc && (
                <Text
                  textStyle='description'
                  textColor='grey'
                  content={`Started on ${formatDateTime(detail.startedAtUtc)}`}
                  style={{ display: 'block', marginTop: '4px' }}
                />
              )}
              {detail.scheduledStartUtc && !detail.startedAtUtc && (
                <Text
                  textStyle='description'
                  textColor='grey'
                  content={`Scheduled to start ${formatDateTime(detail.scheduledStartUtc)}`}
                  style={{ display: 'block', marginTop: '4px' }}
                />
              )}
            </div>

            {/* Per-tenant list — one accordion per lifecycle group, driven by the
                pills above (single-open). */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: '16px' }} className='custom-scrollbar'>
              {sections.map((s, i) => (
                <React.Fragment key={s.key}>
                  {i > 0 && (
                    <div style={{ margin: '6px 0' }}>
                      <Divider />
                    </div>
                  )}
                  {renderSection(s)}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StageRollout
